import { inject, Injectable, Injector } from '@angular/core';
import * as L from 'leaflet';
import { SelectionMode } from '../controls';
import { MapMarker } from '../handlers';
import { SelectionService } from '../../services/selection.service';
import { DrawingService } from './drawing.service';

/**
 * Bridge-Interface zur Kopplung zwischen MapSelectionService und MapComponent.
 * MapComponent implementiert dieses Interface, damit der SelectionService
 * auf Map- und Punkt-spezifische Funktionalität zugreifen kann.
 */
export interface SelectionBridge {
	getPoints(): MapMarker[] | undefined;
	onSelectionChanged(): void;
}

/**
 * Service für alle Auswahl-Funktionalitäten der Karte.
 * Verwaltet Box-, Polygon- und Freihand-Auswahl-Modi,
 * Zoom-to-Window und die zugehörigen Event-Handler.
 */
@Injectable({ providedIn: 'root' })
export class MapSelectionService {

	// ==================== REFERENZEN ====================
	private map!: L.Map;
	private bridge!: SelectionBridge;

	// ==================== SELECTION STATE ====================
	selectionMode: SelectionMode = 'none';
	zoomToWindowActive = false;
	selectionPoints: L.LatLng[] = [];

	private selectionRectangle?: L.Rectangle;
	private selectionPolygon?: L.Polygon;
	private selectionStartPoint?: L.LatLng;

	// Freihand-Auswahl State
	private readonly FREEHAND_MIN_DISTANCE = 15; // Pixel
	private freehandSelectionDrawing = false;
	private freehandSelectionPoints: L.LatLng[] = [];
	private freehandSelectionPolyline?: L.Polyline;
	private lastFreehandSelectionPoint?: L.LatLng;

	// Zoom-to-Window
	private boxControl?: any; // BoxSelectionControl

	// Selection-Control-Referenz für Legacy-Support
	private selectionControl?: any;

	private injector = inject(Injector);
	private _drawingService?: DrawingService;
	/** Lazy-Injection um zirkuläre Abhängigkeit DrawingService ↔ MapSelectionService zu vermeiden */
	private get drawingService(): DrawingService {
		if (!this._drawingService) {
			this._drawingService = this.injector.get(DrawingService);
		}
		return this._drawingService;
	}

	constructor(
		private selectionService: SelectionService,
	) {}

	// ==================== INITIALISIERUNG ====================

	init(map: L.Map, bridge: SelectionBridge, boxControl?: any, selectionControl?: any): void {
		this.map = map;
		this.bridge = bridge;
		this.boxControl = boxControl;
		this.selectionControl = selectionControl;
	}

	setBoxControl(boxControl: any): void {
		this.boxControl = boxControl;
	}

	destroy(): void {
		// Alle Event-Handler entfernen
		if (this.map) {
			this.cleanupSelectionHandlers();
		}
	}

	// ==================== SELECTION MODE ====================

	onSelectionModeChanged(mode: SelectionMode): void {
		// Andere Modi beenden wenn Selection aktiviert wird
		if (mode !== 'none') {
			this.drawingService.endAllDrawModes();
			this.cancelZoomToWindow();
			// Selection-Pane erstellen (über allen anderen Layern)
			if (!this.map.getPane('selection')) {
				this.map.createPane('selection');
				this.map.getPane('selection')!.style.zIndex = '700';
			}
		}

		this.selectionMode = mode;

		// Cleanup previous selection shapes
		this.selectionRectangle?.remove();
		this.selectionPolygon?.remove();
		this.freehandSelectionPolyline?.remove();
		this.selectionRectangle = undefined;
		this.selectionPolygon = undefined;
		this.freehandSelectionPolyline = undefined;
		this.selectionStartPoint = undefined;
		this.selectionPoints = [];
		this.freehandSelectionPoints = [];
		this.freehandSelectionDrawing = false;

		this.cleanupSelectionHandlers();

		if (mode === 'rectangle') {
			L.DomUtil.addClass(this.map.getContainer(), 'leaflet-crosshair');
			this.map.dragging.disable();
			// Tap-Handler deaktivieren um versehentliche Taps zu vermeiden
			if ((this.map as any).tap) {
				(this.map as any).tap.disable();
			}
			// Mouse-Events
			this.map.on('mousedown', this.onSelectionMouseDown, this);
			this.map.on('mousemove', this.onSelectionMouseMove, this);
			this.map.on('mouseup', this.onSelectionMouseUp, this);
			// Touch-Events direkt am Container
			const container = this.map.getContainer();
			container.addEventListener('touchstart', this.onSelectionTouchStart, { passive: false });
			container.addEventListener('touchmove', this.onSelectionTouchMove, { passive: false });
			container.addEventListener('touchend', this.onSelectionTouchEnd, { passive: false });
		} else if (mode === 'polygon') {
			L.DomUtil.addClass(this.map.getContainer(), 'leaflet-crosshair');
			this.map.on('click', this.onSelectionPolygonClick, this);
		} else if (mode === 'freehand') {
			L.DomUtil.addClass(this.map.getContainer(), 'leaflet-crosshair');
			this.map.dragging.disable();
			// Tap-Handler deaktivieren um versehentliche Taps zu vermeiden
			if ((this.map as any).tap) {
				(this.map as any).tap.disable();
			}
			// Mouse-Events
			this.map.on('mousedown', this.onFreehandSelectionMouseDown, this);
			this.map.on('mousemove', this.onFreehandSelectionMouseMove, this);
			this.map.on('mouseup', this.onFreehandSelectionMouseUp, this);
			// Touch-Events
			const container = this.map.getContainer();
			container.addEventListener('touchstart', this.onFreehandSelectionTouchStart, { passive: false });
			container.addEventListener('touchmove', this.onFreehandSelectionTouchMove, { passive: false });
			container.addEventListener('touchend', this.onFreehandSelectionTouchEnd, { passive: false });
			container.addEventListener('contextmenu', this.onPreventContextMenu, { passive: false });
		} else {
			L.DomUtil.removeClass(this.map.getContainer(), 'leaflet-crosshair');
			this.map.dragging.enable();
			// Tap-Handler wieder aktivieren
			if ((this.map as any).tap) {
				(this.map as any).tap.enable();
			}
		}
	}

	private cleanupSelectionHandlers(): void {
		// Remove previous handlers (mouse + touch)
		this.map.off('mousedown', this.onSelectionMouseDown);
		this.map.off('mousemove', this.onSelectionMouseMove);
		this.map.off('mouseup', this.onSelectionMouseUp);
		this.map.off('click', this.onSelectionPolygonClick);
		this.map.off('mousedown', this.onFreehandSelectionMouseDown);
		this.map.off('mousemove', this.onFreehandSelectionMouseMove);
		this.map.off('mouseup', this.onFreehandSelectionMouseUp);
		// Touch-Events vom Container entfernen
		const container = this.map.getContainer();
		container.removeEventListener('touchstart', this.onSelectionTouchStart);
		container.removeEventListener('touchmove', this.onSelectionTouchMove);
		container.removeEventListener('touchend', this.onSelectionTouchEnd);
		container.removeEventListener('touchstart', this.onFreehandSelectionTouchStart);
		container.removeEventListener('touchmove', this.onFreehandSelectionTouchMove);
		container.removeEventListener('touchend', this.onFreehandSelectionTouchEnd);
		container.removeEventListener('contextmenu', this.onPreventContextMenu);
	}

	// ==================== BOX SELECTION ====================

	private onSelectionMouseDown(e: L.LeafletMouseEvent) {
		if (this.selectionMode !== 'rectangle') return;
		this.selectionStartPoint = e.latlng;
		const bounds = L.latLngBounds(e.latlng, e.latlng);
		this.selectionRectangle = L.rectangle(bounds, {
			color: '#007bff',
			weight: 2,
			fillOpacity: 0.2,
			pane: 'selection'
		}).addTo(this.map);
	}

	private onSelectionMouseMove(e: L.LeafletMouseEvent) {
		if (this.selectionMode !== 'rectangle' || !this.selectionStartPoint || !this.selectionRectangle) return;
		const bounds = L.latLngBounds(this.selectionStartPoint, e.latlng);
		this.selectionRectangle.setBounds(bounds);
	}

	private onSelectionMouseUp(e: L.LeafletMouseEvent) {
		if (this.selectionMode !== 'rectangle' || !this.selectionStartPoint || !this.selectionRectangle) return;

		const bounds = L.latLngBounds(this.selectionStartPoint, e.latlng);
		this.togglePointsInBounds(bounds);

		// Cleanup des aktuellen Rechtecks
		this.selectionRectangle.remove();
		this.selectionRectangle = undefined;
		this.selectionStartPoint = undefined;

		// Dragging bleibt deaktiviert für weitere Rechteck-Auswahl
		// Wird erst bei Moduswechsel wieder aktiviert
	}

	// Touch-Events für Rechteck-Auswahl auf Mobilgeräten
	private onSelectionTouchStart = (e: TouchEvent) => {
		if (this.selectionMode !== 'rectangle') return;
		if (e.touches.length !== 1) return; // Nur Single-Touch

		e.preventDefault();
		e.stopPropagation();
		const touch = e.touches[0];
		const latlng = this.map.containerPointToLatLng(L.point(touch.clientX - this.map.getContainer().getBoundingClientRect().left, touch.clientY - this.map.getContainer().getBoundingClientRect().top));

		this.selectionStartPoint = latlng;
		const bounds = L.latLngBounds(latlng, latlng);
		this.selectionRectangle = L.rectangle(bounds, {
			color: '#007bff',
			weight: 2,
			fillOpacity: 0.2,
			pane: 'selection'
		}).addTo(this.map);
	}

	private onSelectionTouchMove = (e: TouchEvent) => {
		if (this.selectionMode !== 'rectangle' || !this.selectionStartPoint || !this.selectionRectangle) return;
		if (e.touches.length !== 1) return;

		e.preventDefault();
		e.stopPropagation();
		const touch = e.touches[0];
		const rect = this.map.getContainer().getBoundingClientRect();
		const latlng = this.map.containerPointToLatLng(L.point(touch.clientX - rect.left, touch.clientY - rect.top));

		const bounds = L.latLngBounds(this.selectionStartPoint, latlng);
		this.selectionRectangle.setBounds(bounds);
	}

	private onSelectionTouchEnd = (e: TouchEvent) => {
		if (this.selectionMode !== 'rectangle' || !this.selectionStartPoint || !this.selectionRectangle) return;

		e.preventDefault();
		e.stopPropagation();

		// Verwende die letzte Position des Rechtecks
		const bounds = this.selectionRectangle.getBounds();
		this.togglePointsInBounds(bounds);

		// Cleanup des aktuellen Rechtecks
		this.selectionRectangle.remove();
		this.selectionRectangle = undefined;
		this.selectionStartPoint = undefined;
	}

	// ==================== POLYGON SELECTION ====================

	private onSelectionPolygonClick(e: L.LeafletMouseEvent) {
		if (this.selectionMode !== 'polygon') return;

		this.selectionPoints.push(e.latlng);

		if (this.selectionPolygon) {
			this.selectionPolygon.setLatLngs(this.selectionPoints);
		} else {
			this.selectionPolygon = L.polygon(this.selectionPoints, {
				color: '#007bff',
				weight: 2,
				fillOpacity: 0.2,
				pane: 'selection'
			}).addTo(this.map);
		}

		// Aktualisiere Button-Anzeige (selectionControl falls noch aktiv)
		this.selectionControl?.setPolygonPoints(this.selectionPoints.length);
	}

	finishPolygonSelection(): void {
		if (this.selectionMode !== 'polygon' || !this.selectionPolygon || this.selectionPoints.length < 3) return;

		this.togglePointsInPolygon(this.selectionPoints);

		// Cleanup
		this.selectionPolygon.remove();
		this.selectionPolygon = undefined;
		this.selectionPoints = [];

		// Button verstecken (selectionControl falls noch aktiv)
		this.selectionControl?.setPolygonPoints(0);
	}

	// ==================== FREEHAND SELECTION ====================

	private onFreehandSelectionMouseDown(e: L.LeafletMouseEvent): void {
		if (this.selectionMode !== 'freehand') return;
		this.startFreehandSelection(e.latlng);
	}

	private onFreehandSelectionMouseMove(e: L.LeafletMouseEvent): void {
		if (this.selectionMode !== 'freehand' || !this.freehandSelectionDrawing) return;
		this.continueFreehandSelection(e.latlng, e.containerPoint);
	}

	private onFreehandSelectionMouseUp(e: L.LeafletMouseEvent): void {
		if (this.selectionMode !== 'freehand' || !this.freehandSelectionDrawing) return;
		this.finishFreehandSelection();
	}

	private onFreehandSelectionTouchStart = (e: TouchEvent): void => {
		if (this.selectionMode !== 'freehand') return;
		if (e.touches.length !== 1) return;

		e.preventDefault();
		e.stopPropagation();
		const touch = e.touches[0];
		const rect = this.map.getContainer().getBoundingClientRect();
		const containerPoint = L.point(touch.clientX - rect.left, touch.clientY - rect.top);
		const latlng = this.map.containerPointToLatLng(containerPoint);
		this.startFreehandSelection(latlng);
	}

	private onFreehandSelectionTouchMove = (e: TouchEvent): void => {
		if (this.selectionMode !== 'freehand' || !this.freehandSelectionDrawing) return;
		if (e.touches.length !== 1) return;

		e.preventDefault();
		e.stopPropagation();
		const touch = e.touches[0];
		const rect = this.map.getContainer().getBoundingClientRect();
		const containerPoint = L.point(touch.clientX - rect.left, touch.clientY - rect.top);
		const latlng = this.map.containerPointToLatLng(containerPoint);
		this.continueFreehandSelection(latlng, containerPoint);
	}

	private onFreehandSelectionTouchEnd = (e: TouchEvent): void => {
		if (this.selectionMode !== 'freehand' || !this.freehandSelectionDrawing) return;

		e.preventDefault();
		e.stopPropagation();
		this.finishFreehandSelection();
	}

	private startFreehandSelection(latlng: L.LatLng): void {
		this.freehandSelectionDrawing = true;
		this.freehandSelectionPoints = [latlng];
		this.lastFreehandSelectionPoint = latlng;

		this.freehandSelectionPolyline = L.polyline([latlng], {
			color: '#007bff',
			weight: 2,
			dashArray: '5, 5',
			pane: 'selection'
		}).addTo(this.map);
	}

	private continueFreehandSelection(latlng: L.LatLng, containerPoint: L.Point): void {
		if (!this.lastFreehandSelectionPoint || !this.freehandSelectionPolyline) return;

		// Berechne Pixelabstand zum letzten Punkt
		const lastContainerPoint = this.map.latLngToContainerPoint(this.lastFreehandSelectionPoint);
		const distance = containerPoint.distanceTo(lastContainerPoint);

		if (distance >= this.FREEHAND_MIN_DISTANCE) {
			this.freehandSelectionPoints.push(latlng);
			this.freehandSelectionPolyline.addLatLng(latlng);
			this.lastFreehandSelectionPoint = latlng;
		}
	}

	private finishFreehandSelection(): void {
		this.freehandSelectionDrawing = false;

		if (this.freehandSelectionPoints.length >= 3) {
			// Polygon schließen für Visualisierung
			const closedPoints = [...this.freehandSelectionPoints, this.freehandSelectionPoints[0]];
			this.freehandSelectionPolyline?.setLatLngs(closedPoints);

			// Punkte im Freihand-Polygon auswählen
			this.togglePointsInPolygon(this.freehandSelectionPoints);
		}

		// Aufräumen
		setTimeout(() => {
			this.freehandSelectionPolyline?.remove();
			this.freehandSelectionPolyline = undefined;
		}, 300);

		this.freehandSelectionPoints = [];
		this.lastFreehandSelectionPoint = undefined;
	}

	// ==================== POINT-IN-AREA TESTS ====================

	private togglePointsInBounds(bounds: L.LatLngBounds): void {
		const idsInBounds: string[] = [];
		this.bridge.getPoints()?.forEach((point: MapMarker) => {
			if (!point.latLng) return;
			if (bounds.contains(point.latLng)) {
				idsInBounds.push(point.id);
			}
		});
		// Toggle: Wenn alle bereits ausgewählt sind, abwählen. Sonst auswählen.
		const allSelected = idsInBounds.length > 0 && idsInBounds.every(id => this.selectionService.isSelected(id));
		if (allSelected) {
			this.selectionService.removeMultiple(idsInBounds);
		} else {
			this.selectionService.add(idsInBounds);
		}
	}

	private togglePointsInPolygon(polygonPoints: L.LatLng[]): void {
		const idsInPolygon: string[] = [];
		this.bridge.getPoints()?.forEach((point: MapMarker) => {
			if (!point.latLng) return;
			if (this.isPointInPolygon(point.latLng, polygonPoints)) {
				idsInPolygon.push(point.id);
			}
		});
		// Toggle: Wenn alle bereits ausgewählt sind, abwählen. Sonst auswählen.
		const allSelected = idsInPolygon.length > 0 && idsInPolygon.every(id => this.selectionService.isSelected(id));
		if (allSelected) {
			this.selectionService.removeMultiple(idsInPolygon);
		} else {
			this.selectionService.add(idsInPolygon);
		}
	}

	private isPointInPolygon(point: L.LatLng, polygon: L.LatLng[]): boolean {
		// Ray casting algorithm
		let inside = false;
		const x = point.lng, y = point.lat;
		for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
			const xi = polygon[i].lng, yi = polygon[i].lat;
			const xj = polygon[j].lng, yj = polygon[j].lat;
			const intersect = ((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
			if (intersect) inside = !inside;
		}
		return inside;
	}

	// ==================== ZOOM TO WINDOW ====================

	onZoomToWindow(): void {
		if (!this.boxControl) return;
		this.drawingService.endAllDrawModes();
		this.zoomToWindowActive = true;
		this.boxControl.startSelection();
		const boundsSubscription = this.boxControl.boundsSelected.subscribe((bounds: L.LatLngBounds) => {
			this.map.fitBounds(bounds, {
				padding: [0, 0],
				animate: true,
				duration: 1.0,
				easeLinearity: 0.25,
			});
			this.zoomToWindowActive = false;
			boundsSubscription.unsubscribe();
			cancelledSubscription.unsubscribe();
		});
		const cancelledSubscription = this.boxControl.selectionCancelled.subscribe(() => {
			this.zoomToWindowActive = false;
			boundsSubscription.unsubscribe();
			cancelledSubscription.unsubscribe();
		});
	}

	/** Bricht den "Zoom aufziehen"-Modus ab */
	cancelZoomToWindow(): void {
		if (this.zoomToWindowActive) {
			this.zoomToWindowActive = false;
			this.boxControl?.cancelSelection();
		}
	}

	// ==================== UTILITY ====================

	// Verhindert das Kontextmenü (Edge/Browser) bei Freihand-Auswahl
	private onPreventContextMenu = (e: Event): void => {
		e.preventDefault();
	}
}
